import * as React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Optional trigger element, wrapped in DialogTrigger/SheetTrigger asChild so it opens the dialog. */
  trigger?: React.ReactNode;
  /** className for the desktop DialogContent. Used for the mobile SheetContent too unless mobileClassName is set. */
  className?: string;
  /** className for the mobile SheetContent, overriding className on mobile only. */
  mobileClassName?: string;
  /** Set when children manage their own internal scroll region, to avoid nesting a second scrollable viewport. */
  noScrollViewport?: boolean;
  /** Override the element focused when the dialog opens. Call event.preventDefault() to take control. */
  onOpenAutoFocus?: (event: Event) => void;
}

export function ResponsiveDialog({
  open,
  onOpenChange,
  children,
  trigger,
  className,
  mobileClassName,
  noScrollViewport,
  onOpenAutoFocus,
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
        <SheetContent
          side="bottom"
          className={cn("flex max-h-[90dvh] flex-col overflow-hidden rounded-t-3xl", mobileClassName ?? className)}
          onOpenAutoFocus={onOpenAutoFocus}
        >
          {noScrollViewport ? (
            children
          ) : (
            <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto overscroll-contain pr-2">
              {children}
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        noScrollViewport={noScrollViewport}
        className={className}
        onOpenAutoFocus={onOpenAutoFocus}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}
